import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import NewButton from "@/components/ui/NewButton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) {
        console.error('Password reset error:', error);
      }

      // Always show success message for security (don't reveal if email exists)
      setSubmitted(true);
      toast.success('If an account exists with this email, you will receive a password reset link.');
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-block mb-6">
              <h1 className="text-3xl font-bold text-neutral-900">Fork It</h1>
            </Link>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">
              Check Your Email
            </h2>
            <p className="text-neutral-600">
              If an account exists with this email, you will receive a password reset link.
            </p>
          </div>

          <Card className="bg-white/80 backdrop-blur-sm border-neutral-300 shadow-xl">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-sm text-neutral-600">
                  Please check your email and follow the instructions to reset your password.
                </p>
                <Link 
                  to="/login" 
                  className="inline-block text-lime hover:text-lime-600 font-semibold transition-colors duration-200"
                >
                  Return to Sign In
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-block mb-6">
            <h1 className="text-3xl font-bold text-neutral-900">Fork It</h1>
          </Link>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Reset Password
          </h2>
          <p className="text-neutral-600">
            Enter your email to receive a password reset link
          </p>
        </div>

        {/* Reset Password Form */}
        <Card className="bg-white/80 backdrop-blur-sm border-neutral-300 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-neutral-900 text-center">
              Forgot Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-neutral-900">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-neutral-300 focus:border-lime focus:ring-lime/20"
                />
              </div>
              
              <div className="flex justify-center pt-2">
                <NewButton
                  type="submit"
                  disabled={loading || !email}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </NewButton>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Back to Sign In Link */}
        <div className="text-center">
          <p className="text-neutral-600">
            Remember your password?{' '}
            <Link 
              to="/login" 
              className="text-lime hover:text-lime-600 font-semibold transition-colors duration-200"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
